/**
 * Email utility functions for reply/forward and recipient checks.
 */

/** Email response shape with optional toUsers, ccUsers, bccUsers (comma-separated strings). */
export interface EmailResponseLike {
  toUsers?: string | null;
  ccUsers?: string | null;
  bccUsers?: string | null;
}

/**
 * Parses a comma-separated recipient string into a trimmed, non-empty list.
 */
function parseRecipients(value: string | null | undefined): string[] {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((e: string) => e.trim())
    .filter(Boolean);
}

/** Regex to match a single email address (used for extraction from channel name or recipient strings). */
const EMAIL_REGEX = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;

/**
 * Extracts the bare email from a recipient string (dynamic for any address).
 * Handles "Name <user@domain.com>" or plain "user@domain.com"; returns lowercase.
 */
export function extractEmailFromRecipient(recipient: string): string {
  if (!recipient || typeof recipient !== "string") return "";
  const trimmed = recipient.trim();
  const angleMatch = trimmed.match(/<([^>]+)>/);
  const email = angleMatch ? angleMatch[1].trim() : trimmed;
  const fallbackMatch = email.match(EMAIL_REGEX);
  return (fallbackMatch ? fallbackMatch[1] : email).toLowerCase();
}

/**
 * Gets the current inbox identity email from the channel name (dynamic for any channel).
 * Used to exclude the current user from Reply All To/Cc so the "From" address is never in recipients.
 */
export function getCurrentInboxEmailFromChannelName(channelName: string | undefined): string {
  if (!channelName) return "";
  const match = channelName.match(EMAIL_REGEX);
  return (match ? match[1] : "").toLowerCase();
}

/**
 * Whether to show the "Reply All" action for the given email.
 * Show when: To has more than one recipient, or CC/BCC has at least one.
 */
export function shouldShowReplyAll(emailResponse: EmailResponseLike | null | undefined): boolean {
  if (!emailResponse) return false;
  const toList = parseRecipients(emailResponse.toUsers);
  const ccList = parseRecipients(emailResponse.ccUsers);
  const bccList = parseRecipients(emailResponse.bccUsers);
  return toList.length > 1 || ccList.length > 0 || bccList.length > 0;
}
